import { type FormEvent, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

import { api, ApiError } from "@/api/client";
import type { TotpSetupResponse } from "@/api/types";
import { AROCIcon } from "@/components/AROCIcon";
import { BackupCodesDisplay } from "@/components/BackupCodesDisplay";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/hooks/useAuth";

type Step = "show-codes" | "verify" | "done";

/**
 * Pantalla del setup OBLIGATORIO tras login cuando AROC_2FA_REQUIRED=true
 * y el operador no tiene 2FA enrolado.
 *
 * Vive fuera de `<RequireAuth>` porque el operador llega acá con cookie
 * scope=2fa_setup_pending — el endpoint /me retornaría 403. Una vez que
 * completa el enable, la cookie pasa a scope=operativa y `refresh()` del
 * AuthProvider promueve el estado a "authenticated".
 *
 * Si el operador llega a esta ruta sin la cookie setup_pending (refresh
 * directo, navegación manual), el /admin/totp/setup devuelve 401/403 y
 * lo redirigimos a /login.
 */
export function TotpForcedSetupPage() {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const [step, setStep] = useState<Step>("show-codes");
  const [data, setData] = useState<TotpSetupResponse | null>(null);
  const [code, setCode] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    void initSetup();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function initSetup() {
    setLoading(true);
    try {
      const resp = await api<TotpSetupResponse>("/api/v1/admin/totp/setup", {
        method: "POST",
      });
      setData(resp);
    } catch (err) {
      if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
        navigate("/login", { replace: true });
        return;
      }
      setError("No se pudo iniciar el enrolamiento. Prueba entrar de nuevo.");
    } finally {
      setLoading(false);
    }
  }

  async function confirmEnable(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError(null);
    if (!/^\d{6}$/.test(code)) {
      setError("Ingresa el código de 6 dígitos del autenticador.");
      return;
    }
    setSubmitting(true);
    try {
      await api<void>("/api/v1/admin/totp/enable", { method: "POST", body: { code } });
      setStep("done");
      await refresh();
      setTimeout(() => navigate("/", { replace: true }), 800);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        setError("Código inválido. Prueba con el código actual del autenticador.");
      } else {
        setError("No se pudo confirmar el enrolamiento.");
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center text-sm text-muted-foreground">
        Preparando enrolamiento…
      </div>
    );
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-2xl space-y-4">
        <div className="flex flex-col items-center gap-2 mb-2">
          <AROCIcon size={64} />
          <div className="text-2xl font-medium tracking-wide text-foreground">AROC</div>
          <div className="text-xs text-muted-foreground">
            Activación obligatoria del segundo factor
          </div>
        </div>
        {error && (
          <div
            role="alert"
            className="rounded-md bg-destructive/10 px-3 py-2 text-sm text-destructive"
          >
            {error}
          </div>
        )}
        {step === "show-codes" && data && (
          <>
            <Card>
              <CardHeader>
                <h2 className="text-lg font-semibold">Paso 1 de 2: configura tu app</h2>
                <p className="text-sm text-muted-foreground">
                  Escaneá el QR con cualquier app TOTP estándar.
                </p>
              </CardHeader>
              <CardContent className="flex justify-center">
                <img
                  src={data.qr_data_uri}
                  alt="Código QR del segundo factor"
                  className="w-48 h-48 border rounded-md bg-white p-2"
                />
              </CardContent>
            </Card>
            <Card>
              <CardHeader>
                <h2 className="text-lg font-semibold">Códigos de respaldo</h2>
                <p className="text-sm text-muted-foreground">
                  Guarda estos 10 códigos. Te dejarán entrar si pierdes el
                  dispositivo del autenticador. Solo se muestran una vez.
                </p>
              </CardHeader>
              <CardContent>
                <BackupCodesDisplay
                  codes={data.backup_codes}
                  onAcknowledge={() => setStep("verify")}
                />
              </CardContent>
            </Card>
          </>
        )}
        {step === "verify" && (
          <Card>
            <CardHeader>
              <h2 className="text-lg font-semibold">Paso 2 de 2: confirma el código</h2>
              <p className="text-sm text-muted-foreground">
                Ingresa el código que muestra tu app ahora.
              </p>
            </CardHeader>
            <CardContent>
              <form className="space-y-4" onSubmit={confirmEnable} noValidate>
                <div className="space-y-2">
                  <Label htmlFor="enable-code">Código del autenticador</Label>
                  <Input
                    id="enable-code"
                    inputMode="numeric"
                    autoFocus
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    maxLength={6}
                    placeholder="123456"
                    disabled={submitting}
                  />
                </div>
                <Button type="submit" className="w-full" disabled={submitting}>
                  {submitting ? "Activando…" : "Activar y continuar"}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}
        {step === "done" && (
          <div className="rounded-md bg-emerald-50 border border-emerald-200 px-4 py-3 text-sm text-emerald-900 dark:bg-emerald-950/30 dark:border-emerald-900 dark:text-emerald-200">
            ✅ Verificación en dos pasos activada. Redirigiendo…
          </div>
        )}
      </div>
    </div>
  );
}
